/**
 * <nysta-sticky-action> -- the mobile purchase bar for free patterns and kits
 * (snippets/nysta-sticky-buy.liquid). Ordinary products use Dwell's
 * <sticky-add-to-cart> instead.
 *
 * Visibility follows the same rule as Dwell's bar: shown only while the page's own
 * primary action (data-target) is scrolled ABOVE the viewport, hidden while it is in
 * view or not yet reached, and hidden when the footer comes within 200px so the end
 * of the page is never covered.
 *
 * data-proxy:        a button whose click this bar delegates to (the kit CTA), so the
 *                    bar never owns purchase logic of its own. Its disabled state and
 *                    label are mirrored.
 * data-price-source: an element whose text is mirrored into [data-price-mirror].
 * data-error-source: an element that, when it becomes visible after a delegated click,
 *                    is scrolled into view so the customer sees why nothing was added.
 */
class NystaStickyAction extends HTMLElement {
  /** @type {IntersectionObserver[]} */
  #observers = [];
  /** @type {MutationObserver[]} */
  #mutations = [];
  #targetVisible = true;
  #targetAbove = false;
  #footerNear = false;
  #awaitingError = false;

  /** querySelector that tolerates a missing/empty selector (free mode has no proxy). */
  #find(selector) {
    if (!selector) return null;
    try {
      return document.querySelector(selector);
    } catch (error) {
      return null;
    }
  }

  connectedCallback() {
    this.bar = this.querySelector('.nysta-sticky-buy__bar');
    const target = this.#find(this.dataset.target);
    if (!this.bar || !target) return;

    const targetObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      this.#targetVisible = entry.isIntersecting;
      this.#targetAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.#update();
    });
    targetObserver.observe(target);
    this.#observers.push(targetObserver);

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (footer) {
      const footerObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          this.#footerNear = entry.isIntersecting;
          this.#update();
        },
        { rootMargin: '200px 0px 0px 0px' }
      );
      footerObserver.observe(footer);
      this.#observers.push(footerObserver);
    }

    this.#setupProxy();
    this.#setupPriceMirror();
    this.#setupErrorWatch();
  }

  disconnectedCallback() {
    this.#observers.forEach((o) => o.disconnect());
    this.#mutations.forEach((m) => m.disconnect());
    this.#observers = [];
    this.#mutations = [];
  }

  #update() {
    const show = this.#targetAbove && !this.#targetVisible && !this.#footerNear;
    this.bar.dataset.stuck = show ? 'true' : 'false';
    // A hidden bar must not be reachable by keyboard or screen reader.
    this.bar.toggleAttribute('inert', !show);
  }

  #setupProxy() {
    const button = this.querySelector('[data-proxy-button]');
    const proxied = /** @type {HTMLButtonElement | null} */ (this.#find(this.dataset.proxy));
    if (!button || !proxied) return;

    const sync = () => {
      button.toggleAttribute('disabled', proxied.disabled || proxied.getAttribute('aria-disabled') === 'true');
      const label = proxied.textContent?.trim();
      if (label) button.textContent = label;
    };
    sync();
    const mo = new MutationObserver(sync);
    mo.observe(proxied, { attributes: true, childList: true, subtree: true, characterData: true });
    this.#mutations.push(mo);

    button.addEventListener('click', () => {
      if (proxied.disabled) return;
      this.#awaitingError = true;
      proxied.click();
    });
  }

  #setupPriceMirror() {
    const mirror = this.querySelector('[data-price-mirror]');
    const source = this.#find(this.dataset.priceSource);
    if (!mirror || !source) return;
    const sync = () => {
      mirror.textContent = source.textContent?.trim() || '';
    };
    sync();
    const mo = new MutationObserver(sync);
    mo.observe(source, { childList: true, subtree: true, characterData: true });
    this.#mutations.push(mo);
  }

  #setupErrorWatch() {
    const error = /** @type {HTMLElement | null} */ (this.#find(this.dataset.errorSource));
    if (!error) return;
    const mo = new MutationObserver(() => {
      if (!this.#awaitingError) return;
      const visible = !error.hidden && error.offsetParent !== null && (error.textContent || '').trim() !== '';
      if (visible) {
        this.#awaitingError = false;
        error.scrollIntoView({ block: 'center' });
      }
    });
    mo.observe(error, { attributes: true, childList: true, subtree: true, characterData: true });
    this.#mutations.push(mo);
  }
}

if (!customElements.get('nysta-sticky-action')) {
  customElements.define('nysta-sticky-action', NystaStickyAction);
}
